import React, { useState } from 'react';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import { Sprite, spriteSrc } from '../Sprites/Sprite';
import './AverageCostMethod.scss';

/**
 * 「平均成本法」, an in-house replacement for the Piktochart of the same name, in the
 * look of ai.portfoplus.com: the twelve monthly prices of its line chart, and its
 * table of what $10,000 a month buys against putting $120,000 in all in January.
 * The prices and the table's printed figures are the Piktochart's own.
 */

/** The price each month, the Piktochart's series. */
const PRICES = [100, 80, 50, 25, 20, 25, 25, 50, 80, 80, 60, 90];
const MONTHLY = 10000;

const VIEW_W = 720;
const VIEW_H = 340;
const PAD_LEFT = 56;
const PAD_RIGHT = 24;
// Room above the 100 line for the axis title and the first price.
const PAD_TOP = 46;
const PAD_BOTTOM = 66;
const MAX = 100;
const TICKS = [0, 25, 50, 75, 100];

const x = (i: number) => PAD_LEFT + (i / (PRICES.length - 1)) * (VIEW_W - PAD_LEFT - PAD_RIGHT);
const y = (price: number) => PAD_TOP + ((MAX - price) / MAX) * (VIEW_H - PAD_TOP - PAD_BOTTOM);

const LINE = PRICES.map((price, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(price)}`).join(' ');
const BASE = VIEW_H - PAD_BOTTOM;
const AREA = `${LINE} L${x(PRICES.length - 1)},${BASE} L${x(0)},${BASE} Z`;

/** What $10,000 a month buys, and what that comes to on average. */
export const unitsBought = PRICES.reduce((units, price) => units + MONTHLY / price, 0);
export const averagePrice = (MONTHLY * PRICES.length) / unitsBought;

/** All-in in January: the whole $120,000 at the first month's price. */
const LUMP_UNITS = (MONTHLY * PRICES.length) / PRICES[0];

/** Where both ways of investing stand at the end of month `i` (0-based), at that month's price. */
export const standingAt = (i: number) => {
  const price = PRICES[i];
  const units = PRICES.slice(0, i + 1).reduce((sum, p) => sum + MONTHLY / p, 0);
  const invested = MONTHLY * (i + 1);
  return {
    price,
    bought: MONTHLY / price,
    invested,
    units,
    average: invested / units,
    value: units * price,
    returnPct: ((units * price) / invested - 1) * 100,
    lumpValue: LUMP_UNITS * price,
    lumpReturnPct: (price / PRICES[0] - 1) * 100
  };
};

const fmt = (n: number, digits = 0) =>
  n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const pct = (n: number) => `${n >= 0 ? '+' : '-'}${fmt(Math.abs(n), 2).replace(/\.00$/, '')}%`;

const LAST = PRICES.length - 1;

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const AverageCostMethod = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const total = `$${fmt(MONTHLY * PRICES.length)}`;
  // The trough, where the idea is clearest; a tap or hover on any month moves it.
  const [active, setActive] = useState(4);
  const now = standingAt(active);
  const end = standingAt(LAST);

  return (
    <div className="average-cost-method" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="acm-head">
        <h2 className="acm-title">
          <span>{t('acm-title')}</span>
        </h2>
        <p className="acm-sub">{t('acm-sub')}</p>
        <p className="acm-lead">
          {t('acm-lead')}
          <strong>{t('acm-lead-key')}</strong>
        </p>
      </header>

      <figure className="acm-chart">
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={t('acm-chart-label')}>
          <defs>
            <linearGradient id="acm-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3966F8" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#3966F8" stopOpacity="0" />
            </linearGradient>
          </defs>
          {TICKS.map(tick => (
            <g key={tick}>
              <line
                className={tick === 0 ? 'acm-axis' : 'acm-grid'}
                x1={PAD_LEFT}
                x2={VIEW_W - PAD_RIGHT}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text className="acm-tick" x={PAD_LEFT - 10} y={y(tick)} dy="0.36em" textAnchor="end">
                {tick}
              </text>
            </g>
          ))}
          <text className="acm-axis-label" x={4} y={14}>
            {t('acm-axis-price')}
          </text>
          <path d={AREA} fill="url(#acm-area)" />
          {/* The empty sky over the low months: a piggy bank taking in a coin, as a low price buys more units. */}
          <image
            href={spriteSrc('piggy-coin')}
            data-sprite="piggy-coin"
            className="acm-buddy"
            x={256}
            y={46}
            width={124}
            height={112}
          />
          <line className="acm-guide" x1={x(active)} x2={x(active)} y1={PAD_TOP - 8} y2={BASE} />
          <path className="acm-line" d={LINE} pathLength={1} />
          {PRICES.map((price, i) => (
            <g
              key={i}
              className={`acm-point${i === active ? ' is-active' : ''}`}
              style={{ animationDelay: `${0.5 + i * 0.05}s` }}
            >
              <circle cx={x(i)} cy={y(price)} r={i === active ? 9 : 6} />
              <text className="acm-price" x={x(i)} y={y(price) - 14} textAnchor="middle">
                {price}
              </text>
              <text className="acm-tick" x={x(i)} y={BASE + 24} textAnchor="middle">
                {i + 1}
              </text>
            </g>
          ))}
          <text className="acm-axis-label" x={(PAD_LEFT + VIEW_W - PAD_RIGHT) / 2} y={BASE + 52} textAnchor="middle">
            {t('acm-axis-time')}
          </text>
          {/* A tall, invisible strip a month wide, so a finger or a pointer need not hit the dot. */}
          {PRICES.map((_, i) => {
            const half = (VIEW_W - PAD_LEFT - PAD_RIGHT) / (PRICES.length - 1) / 2;
            return (
              <rect
                key={i}
                className="acm-hit"
                x={x(i) - half}
                y={0}
                width={half * 2}
                height={VIEW_H}
                tabIndex={0}
                role="button"
                aria-label={t('acm-d-head', { m: i + 1, p: PRICES[i] })}
                aria-pressed={i === active}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
              />
            );
          })}
        </svg>
        <figcaption className="acm-hint">{t('acm-hint')}</figcaption>
      </figure>

      <section className="acm-detail" aria-live="polite">
        <h3>
          {/* The investor's face follows the plan's standing that month. */}
          <Sprite name={now.returnPct >= 0 ? 'face-euphoria' : 'face-hesitate'} size={44} className="acm-face" />
          {t('acm-d-head', { m: active + 1, p: now.price })}
        </h3>
        <table>
          <thead>
            <tr>
              <th aria-hidden="true" />
              <th scope="col">{t('acm-col-acm')}</th>
              <th scope="col">{t('acm-col-lump')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">{t('acm-d-bought')}</th>
              <td>{t('acm-units', { n: fmt(now.bought, 1) })}</td>
              <td>{active === 0 ? t('acm-units', { n: fmt(LUMP_UNITS) }) : '—'}</td>
            </tr>
            <tr>
              <th scope="row">{t('acm-d-invested')}</th>
              <td>${fmt(now.invested)}</td>
              <td>{total}</td>
            </tr>
            <tr>
              <th scope="row">{t('acm-d-units')}</th>
              <td>{t('acm-units', { n: fmt(now.units, 1) })}</td>
              <td>{t('acm-units', { n: fmt(LUMP_UNITS) })}</td>
            </tr>
            <tr>
              <th scope="row">{t('acm-d-avg')}</th>
              <td>${fmt(now.average, 2)}</td>
              <td>${fmt(PRICES[0])}</td>
            </tr>
            <tr>
              <th scope="row">{t('acm-d-value')}</th>
              <td>${fmt(now.value)}</td>
              <td>${fmt(now.lumpValue)}</td>
            </tr>
            <tr>
              <th scope="row">{t('acm-d-return')}</th>
              <td className={now.returnPct >= 0 ? 'is-gain' : 'is-loss'}>{pct(now.returnPct)}</td>
              <td className={now.lumpReturnPct >= 0 ? 'is-gain' : 'is-loss'}>{pct(now.lumpReturnPct)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <table className="acm-table">
        <thead>
          <tr>
            <th aria-hidden="true" />
            <th scope="col" className="is-acm">
              {/* The way that comes out ahead. */}
              <Sprite name="gold-trophy" size={52} className="acm-trophy" bob />
              <ShowChartRoundedIcon aria-hidden="true" />
              {t('acm-col-acm')}
            </th>
            <th scope="col" className="is-lump">
              {t('acm-col-lump')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t('acm-row-total')}</th>
            <td>{total}</td>
            <td>{total}</td>
          </tr>
          <tr>
            <th scope="row">{t('acm-row-units')}</th>
            <td>
              <strong>{t('acm-units', { n: fmt(unitsBought, 1) })}</strong>
            </td>
            <td>
              <strong>{t('acm-units', { n: fmt(LUMP_UNITS) })}</strong>
            </td>
          </tr>
          <tr>
            <th scope="row">{t('acm-row-avg')}</th>
            <td>${fmt(averagePrice, 2)}</td>
            <td>${fmt(PRICES[0])}</td>
          </tr>
          <tr>
            <th scope="row">{t('acm-row-return')}</th>
            <td className="is-gain">{pct(end.returnPct)}</td>
            <td className="is-loss">{pct(end.lumpReturnPct)}</td>
          </tr>
        </tbody>
      </table>

      <div className="acm-tip">
        <LightbulbRoundedIcon aria-hidden="true" />
        <p>
          {t('acm-win-lead')}
          <strong>{t('acm-win-key')}</strong>
          {t('acm-win-end')}
          <br />
          {t('acm-win-2')}
        </p>
        <Sprite name="face-calm" size={84} className="acm-tip-face" bob />
      </div>

      <p className="acm-disclaimer">
        {t('dsc-disclaimer-1')}
        <br />
        {t('dsc-disclaimer-2')}
      </p>
    </div>
  );
};

export default AverageCostMethod;
